import React, { useState, useEffect } from 'react';
import { Table, Button, Space, Input, Form, Modal, Radio, message } from 'antd';
import { EditOutlined, DeleteOutlined, SearchOutlined, PlusOutlined } from '@ant-design/icons';
import { FaUsers } from 'react-icons/fa';
import { otherApi } from '../api';
import bg from '../assests/bg.jpg';

const columns = (editItem, deleteItem) => [
  {
    title: 'S.No',
    key: 'sno',
    render: (text, record, index) => index + 1,
    width: '10%',
  },
  {
    title: 'Vakaira Name',
    dataIndex: 'vakairaName',
    key: 'vakairaName',
    width: '30%',
  },
  {
    title: 'Description',
    dataIndex: 'description',
    key: 'description',
    width: '40%',
  },
  {
    title: 'Status',
    dataIndex: 'status',
    key: 'status',
    render: status => status ? 'Active' : 'Inactive',
    width: '10%',
  },
  {
    title: 'Actions',
    key: 'actions',
    render: (_, record) => (
      <Space size="middle">
        <Button icon={<EditOutlined />} onClick={() => editItem(record)} />
        <Button icon={<DeleteOutlined />} onClick={() => deleteItem(record._id)} />
      </Space>
    ),
    width: '10%',
  },
];

const Register = () => {
  const [data, setData] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isConfirmDeleteVisible, setIsConfirmDeleteVisible] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const response = await otherApi.get('/vakaira');
      if (response.data && Array.isArray(response.data.data)) {
        setData(response.data.data);
      } else {
        throw new Error('Unexpected response structure');
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      message.error('Failed to fetch data. Please try again later.');
    }
  };

  const handleAdd = async (values) => {
    try {
      await otherApi.post('/vakaira/addVakaira', {
        vakairaName: values.vakairaName,
        description: values.description,
        status: values.status === 'active',
      });
      message.success('Vakaira added successfully!');
      fetchData(); // Refresh the data
    } catch (error) {
      console.error('Failed to add Vakaira:', error);
      message.error('Failed to add Vakaira. Please try again later.');
    }
  };

  const handleUpdate = async (values) => {
    try {
      if (!editingItem || !editingItem._id) {
        throw new Error('No ID provided for update');
      }

      await otherApi.put(`/vakaira/updateVakaira/${editingItem._id}`, {
        vakairaName: values.vakairaName,
        description: values.description,
        status: values.status === 'active',
      });

      setData(data.map(item =>
        item._id === editingItem._id ? { ...item, ...values, status: values.status === 'active' } : item
      ));
      message.success('Vakaira updated successfully');
      fetchData(); // Refresh the data
    } catch (error) {
      console.error('Failed to update Vakaira:', error);
      message.error('Failed to update Vakaira. Please try again later.');
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      if (editingItem) {
        await handleUpdate(values);
      } else {
        await handleAdd(values);
      }
      form.resetFields();
      setIsModalVisible(false);
      setEditingItem(null);
    } catch (error) {
      console.error('Failed to submit data:', error);
      message.error('Failed to submit data. Please try again later.');
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      if (!editingItem || !editingItem._id) {
        throw new Error('No ID provided for deletion');
      }

      await otherApi.delete(`/vakaira/deleteVakaira/${editingItem._id}`);
      setData(data.filter(item => item._id !== editingItem._id));
      message.success('Vakaira deleted successfully');
      fetchData(); // Refresh the data
    } catch (error) {
      console.error('Failed to delete Vakaira:', error);
      message.error('Failed to delete Vakaira. Please try again later.');
    }
    setIsConfirmDeleteVisible(false);
    setEditingItem(null);
  };

  const handleSearch = e => {
    setSearchText(e.target.value);
  };

  const filteredData = data.filter(item =>
    (item.vakairaName || '').toLowerCase().includes(searchText.toLowerCase())
  );

  const editItem = record => {
    setEditingItem(record);
    form.setFieldsValue({ ...record, status: record.status ? 'active' : 'inactive' });
    setIsModalVisible(true);
  };

  const deleteItem = _id => {
    setEditingItem(data.find(item => item._id === _id));
    setIsConfirmDeleteVisible(true);
  };

  return (
    <div style={{ padding: '20px', boxSizing: 'border-box' }}>
      <h1 style={{ color: 'seagreen', textAlign: 'center', marginBottom: '20px' }}>
        <FaUsers /> Vakairas
      </h1>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px' }}>
        <Input
          placeholder="Search Vakaira Name"
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={handleSearch}
          style={{ width: 200 }}
        />
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => {
            form.resetFields();
            setEditingItem(null);
            setIsModalVisible(true);
          }}
        >
          Add Vakaira
        </Button>
      </div>
      <Table
        columns={columns(editItem, deleteItem)}
        dataSource={filteredData}
        pagination={{ pageSize: 10 }}
        rowKey="_id"
      />
      <Modal
        title={editingItem ? "Edit Vakaira" : "Add New Vakaira"}
        visible={isModalVisible}
        onOk={handleSubmit}
        onCancel={() => setIsModalVisible(false)}
        footer={[
          <Button key="back" onClick={() => setIsModalVisible(false)}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={handleSubmit}>
            {editingItem ? 'Save Changes' : 'Submit'}
          </Button>,
        ]}
      >
        <Form
          form={form}
          name="vakairaForm"
          labelCol={{ span: 8 }}
          wrapperCol={{ span: 16 }}
        >
          <Form.Item
            label="Vakaira Name"
            name="vakairaName"
            rules={[{ required: true, message: 'Please input the Vakaira Name!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Description"
            name="description"
            rules={[{ required: true, message: 'Please input the Description!' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label="Status"
            name="status"
            rules={[{ required: true, message: 'Please select the Status!' }]}
          >
            <Radio.Group>
              <Radio value="active">Active</Radio>
              <Radio value="inactive">Inactive</Radio>
            </Radio.Group>
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="Confirm Deletion"
        visible={isConfirmDeleteVisible}
        onOk={handleDeleteConfirm}
        onCancel={() => setIsConfirmDeleteVisible(false)}
        okText="Delete"
        cancelText="Cancel"
      >
        <p>Are you sure you want to delete this item?</p>
      </Modal>
    </div>
  );
};

export default Register;
